import { unwrap } from "@sagot/api-client";
import { useTranslation } from "@sagot/i18n";
import { Badge, Button, EmptyState, Field, Icon, Input, Select, Skeleton, toast, type BadgeTone, type IconName } from "@sagot/ui";
import { useMutation } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";
import { useErrorMessage } from "../../../../lib/api-errors";
import { useApi } from "../../../../session/session-provider";
import { useWorkspaceId } from "../../../../session/workspace-context";
import { useAgentList, useFormatters, wsKeys, type CustomDomainOut } from "../../shared";
import { isDomain } from "../settings-model";
import { useCustomDomains, useInvalidate } from "../use-settings";
import { DomainLiveRows } from "./domain-live-rows";
import { DomainProgress } from "./domain-progress";
import { DomainRecordsTable } from "./domain-records-table";
import { CardActions, Gate, PanelError, useCanManage } from "./panel-kit";

const STATUS: Readonly<Record<string, { tone: BadgeTone; icon: IconName }>> = {
  active: { tone: "success", icon: "check-circle" },
  verifying: { tone: "warning", icon: "clock" },
  pending: { tone: "warning", icon: "clock" },
  failed: { tone: "danger", icon: "x-circle" },
};

/** "Ask.Example.PH." → "ask.example.ph" (no scheme, path or trailing dot). */
export function normalizeHostname(value: string): string {
  return value.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "").replace(/\.$/, "");
}

function DomainStatus({ domain }: { domain: CustomDomainOut }) {
  const { t } = useTranslation();
  const spec = STATUS[domain.status] ?? { tone: "neutral" as const, icon: "circle" as const };
  return (
    <span className="flex flex-wrap gap-1.5">
      <Badge tone={spec.tone}>
        <Icon name={spec.icon} className="size-3" />
        {t(`settings.domains.status.${domain.status}`, { defaultValue: domain.status })}
      </Badge>
      {domain.status === "active" ? (
        <Badge tone={domain.ssl_active ? "success" : "warning"}>
          <Icon name="lock" className="size-3" />
          {t(domain.ssl_active ? "settings.domains.sslActive" : "settings.domains.sslPending")}
        </Badge>
      ) : null}
    </span>
  );
}

/** The records to add, the Cloudflare tip and how long it takes (mockup domains pins 1-2). */
function PendingSetup({ domain, records }: { domain: CustomDomainOut; records: CustomDomainOut["required_records"] }) {
  const { t } = useTranslation();
  if (records.length === 0) return <p className="text-xs text-fg-muted">{t("settings.domains.noRecords")}</p>;
  return (
    <>
      <DomainRecordsTable domain={domain} records={records} />
      {domain.provider_tip === "cloudflare_dns_only" ? (
        <div role="note" className="flex items-start gap-2.5 rounded-md bg-info-soft p-3 text-sm" data-testid="domain-provider-tip">
          <Icon name="info" className="mt-0.5 size-4 shrink-0 text-info" />
          <p>
            <strong>{t("settings.domains.tip.cloudflareTitle")}</strong> {t("settings.domains.tip.cloudflareBody", { host: domain.hostname })}
          </p>
        </div>
      ) : null}
      <p className="text-xs text-fg-muted">{t("settings.domains.recordsHint")}</p>
    </>
  );
}

function DomainCard({ domain }: { domain: CustomDomainOut }) {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const message = useErrorMessage();
  const invalidate = useInvalidate();
  const format = useFormatters();
  const manage = useCanManage("settings.manage");
  const path = { params: { path: { domain_id: domain.id } } };
  const verify = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/custom-domains/{domain_id}/verify", path)),
    onSuccess: async (result) => {
      if (result.status === "active") toast.success(t("settings.domains.verified", { host: result.hostname }));
      else toast.info(t("settings.domains.notYet"));
      await invalidate(wsKeys.customDomains(wsId));
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const remove = useMutation({
    mutationFn: () => unwrap(api.DELETE("/v1/custom-domains/{domain_id}", path)),
    onSuccess: async () => {
      toast.success(t("settings.domains.removed"));
      await invalidate(wsKeys.customDomains(wsId));
    },
    onError: (caught) => toast.error(message(caught)),
  });
  const records = domain.required_records;
  const live = domain.status === "active";
  const retryBotCheck = live && domain.turnstile_state === "manual_required";
  return (
    <section className="card overflow-hidden" aria-label={domain.hostname} data-testid="custom-domain">
      <div className="card-header flex-wrap gap-2">
        <div className="min-w-0">
          <h3 className="break-all font-mono text-sm font-semibold">{domain.hostname}</h3>
          <p className="mt-0.5 text-xs text-fg-subtle">{domain.last_checked_at ? t("settings.domains.lastChecked", { when: format.relative(domain.last_checked_at) }) : t("settings.domains.neverChecked")}</p>
        </div>
        <DomainStatus domain={domain} />
      </div>
      {domain.error ? (
        <p className="mx-5 mt-4 flex items-start gap-2 rounded-md bg-danger-soft px-3 py-2 text-xs text-fg" role="alert">
          <Icon name="alert-triangle" className="mt-0.5 size-4 shrink-0 text-danger" />
          {domain.error}
        </p>
      ) : null}
      <div className="card-body space-y-4">
        <DomainProgress domain={domain} />
        {live ? null : <PendingSetup domain={domain} records={records} />}
        {live ? <DomainLiveRows domain={domain} /> : null}
      </div>
      <CardActions>
        <Gate reason={manage.reason}>
          <Button variant="ghost" className="text-danger" disabled={!manage.allowed} loading={remove.isPending} onClick={() => { remove.mutate(); }}>
            <Icon name="trash" />
            {t("settings.domains.remove")}
          </Button>
        </Gate>
        {live && !retryBotCheck ? null : (
          <Gate reason={manage.reason}>
            <Button disabled={!manage.allowed} loading={verify.isPending} onClick={() => { verify.mutate(); }}>
              <Icon name="refresh-cw" />
              {t("settings.domains.verify")}
            </Button>
          </Gate>
        )}
      </CardActions>
    </section>
  );
}

function AddDomainForm() {
  const { t } = useTranslation();
  const api = useApi();
  const wsId = useWorkspaceId();
  const message = useErrorMessage();
  const invalidate = useInvalidate();
  const manage = useCanManage("settings.manage");
  const agents = useAgentList();
  const [hostname, setHostname] = useState("");
  const [agentId, setAgentId] = useState("");
  const [error, setError] = useState<string | undefined>();
  const [busy, setBusy] = useState(false);
  const submit = async (event: SyntheticEvent) => {
    event.preventDefault();
    const host = normalizeHostname(hostname);
    if (!isDomain(host)) {
      setError(t("settings.domains.invalid"));
      return;
    }
    setBusy(true);
    try {
      await unwrap(api.POST("/v1/custom-domains", { body: { hostname: host, agent_id: agentId || null } }));
      toast.success(t("settings.domains.added", { host }));
      setHostname("");
      await invalidate(wsKeys.customDomains(wsId));
    } catch (caught) {
      const reason = message(caught);
      setError(reason);
      toast.error(reason);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="card" onSubmit={(event) => void submit(event)} noValidate aria-labelledby="domains-title">
      <div className="card-header">
        <div>
          <h2 id="domains-title" className="card-title">
            {t("settings.domains.title")}
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">{t("settings.domains.subtitle")}</p>
        </div>
      </div>
      <fieldset className="card-body grid grid-cols-1 gap-4 md:grid-cols-2" disabled={!manage.allowed}>
        <Field label={t("settings.domains.hostname")} hint={t("settings.domains.hostnameHint")} error={error}>
          <Input className="font-mono text-xs" placeholder={t("settings.domains.placeholder")} value={hostname} onChange={(event) => { setHostname(event.target.value); setError(undefined); }} />
        </Field>
        <Field label={`${t("settings.domains.agent")} ${t("common.optional")}`} hint={t("settings.domains.agentHint")}>
          <Select value={agentId} onChange={(event) => { setAgentId(event.target.value); }}>
            <option value="">{t("settings.domains.allAgents")}</option>
            {(agents.data ?? []).map((agent) => (
              <option key={agent.id} value={agent.id}>
                {agent.name}
              </option>
            ))}
          </Select>
        </Field>
      </fieldset>
      <CardActions>
        <Button type="submit" disabled={!manage.allowed || !hostname.trim()} loading={busy} title={manage.reason}>
          <Icon name="plus" />
          {t("settings.domains.add")}
        </Button>
      </CardActions>
    </form>
  );
}

/** Domains panel (DEC-010, M8.1): custom web-app domain → DNS records → verify → SSL. */
export function DomainsPanel() {
  const { t } = useTranslation();
  const domains = useCustomDomains();
  if (domains.isError) return <PanelError error={domains.error} onRetry={() => void domains.refetch()} />;
  return (
    <>
      <AddDomainForm />
      {domains.isPending ? (
        <Skeleton className="h-40 w-full" />
      ) : domains.data.length === 0 ? (
        <div className="card">
          <EmptyState icon="globe" headingLevel={3} title={t("settings.domains.emptyTitle")} description={t("settings.domains.emptyBody")} />
        </div>
      ) : (
        domains.data.map((domain) => <DomainCard key={domain.id} domain={domain} />)
      )}
    </>
  );
}
